import {
  Divider,
  Form,
  InputNumber,
  message,
  Modal,
  Select,
  Switch,
  Typography,
} from 'antd'
import { mergeNiiCalculatorAssumptionsForSite } from 'features/drilldown/panels/impl/niiRoi/niiRoiCalculatorModel'
import type {
  LibraTicketGroup,
  SiteRoiAssumptionsValue,
} from 'features/libra/ticket/libraTicketsApi'
import {
  TAR_ROI_GLOBAL_STORAGE_KEY,
  TAR_ROI_GROUP_DATA_KEY,
  useUpdateLibraTicketGroup,
} from 'features/libra/ticket/libraTicketsApi'
import { useGeckoPermissions } from 'features/permissions'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_LIBRA_EXECUTIVE,
} from 'types'

const { Text, Title } = Typography

type RoiFormShape = Omit<
  SiteRoiAssumptionsValue,
  'hseIncidentProbPerPersonPerTar'
> & {
  siteId: string
  hseIncidentProbPercent: number | undefined
}

type NiiRoiAssumptionsSettingsModalProps = {
  open: boolean
  onClose: () => void
  group: LibraTicketGroup
  siteOptions: ReadonlyArray<{ value: string; label: string }>
  selectedSiteId: string | undefined
  onSaved: () => void
}

export const NiiRoiAssumptionsSettingsModal: FC<
  NiiRoiAssumptionsSettingsModalProps
> = ({ open, onClose, group, siteOptions, selectedSiteId, onSaved }) => {
  const { hasPermissions } = useGeckoPermissions()
  const canEditRoi = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_LIBRA_EXECUTIVE,
  ])
  const doUpdate = useUpdateLibraTicketGroup()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<RoiFormShape>()

  const defaultSite = useMemo(() => {
    if (selectedSiteId) return selectedSiteId
    return siteOptions[0]?.value
  }, [selectedSiteId, siteOptions])

  const loadFormForSite = useCallback(
    (site: string) => {
      const tar = group[TAR_ROI_GROUP_DATA_KEY] ?? {}
      const globalRaw = tar[TAR_ROI_GLOBAL_STORAGE_KEY]
      const siteRaw = tar[site]
      const a = mergeNiiCalculatorAssumptionsForSite(globalRaw, siteRaw)
      form.setFieldsValue({
        siteId: site,
        traditionalInspectionTeamPerAsset: a.traditionalInspectionTeamPerAsset,
        equipmentPrepMobDemobAedPerAsset: a.equipmentPrepMobDemobAedPerAsset,
        traditionalInspectionAedPerAsset: a.traditionalInspectionAedPerAsset,
        niiInspectionAedPerAsset: a.niiInspectionAedPerAsset,
        scaffoldingAedPerAsset: a.scaffoldingAedPerAsset,
        hseIncidentProbPercent: a.hseIncidentProbPerPersonPerTar * 100,
        hseDownsideAed: a.hseDownsideAed,
        criticalPathReductionPercent: a.criticalPathReductionPercent,
        tarDurationDays: a.tarDurationDays,
        dailyProductionAed: a.dailyProductionAed,
        inspectionOnCriticalPath: a.inspectionOnCriticalPath,
      })
    },
    [form, group]
  )

  useEffect(() => {
    if (!open) return
    const site = defaultSite
    if (!site) return
    loadFormForSite(site)
  }, [open, defaultSite, loadFormForSite])

  const handleSiteChange = useCallback(
    (site: string) => {
      loadFormForSite(site)
    },
    [loadFormForSite]
  )

  const onOk = useCallback(async () => {
    if (!canEditRoi) {
      message.warning('You do not have permission to edit ROI settings.')
      return
    }
    const v = await form.validateFields()
    const siteId = v.siteId
    if (!siteId) {
      message.error('Select a site.')
      return
    }
    setSaving(true)
    try {
      const prev = group[TAR_ROI_GROUP_DATA_KEY] ?? {}
      const sitePayload: SiteRoiAssumptionsValue = {
        traditionalInspectionTeamPerAsset: v.traditionalInspectionTeamPerAsset,
        equipmentPrepMobDemobAedPerAsset: v.equipmentPrepMobDemobAedPerAsset,
        traditionalInspectionAedPerAsset: v.traditionalInspectionAedPerAsset,
        niiInspectionAedPerAsset: v.niiInspectionAedPerAsset,
        scaffoldingAedPerAsset: v.scaffoldingAedPerAsset,
        hseIncidentProbPerPersonPerTar:
          v.hseIncidentProbPercent === undefined
            ? undefined
            : v.hseIncidentProbPercent / 100,
        hseDownsideAed: v.hseDownsideAed,
        criticalPathReductionPercent: v.criticalPathReductionPercent,
        tarDurationDays: v.tarDurationDays,
        dailyProductionAed: v.dailyProductionAed,
        inspectionOnCriticalPath: v.inspectionOnCriticalPath,
      }
      await doUpdate({
        group,
        next: {
          title: group.title,
          totalAssets: group.totalAssets,
          owners: group.owners,
          [TAR_ROI_GROUP_DATA_KEY]: {
            ...prev,
            [siteId]: {
              ...(prev[siteId] ?? {}),
              ...sitePayload,
            },
          },
        },
      })
      message.success('ROI assumptions saved')
      onSaved()
      onClose()
    } catch (e) {
      console.error(e)
      message.error('Could not save ROI assumptions')
    } finally {
      setSaving(false)
    }
  }, [form, doUpdate, group, canEditRoi, onClose, onSaved])

  if (siteOptions.length === 0) {
    return (
      <Modal
        title='ROI assumptions'
        open={open}
        onCancel={onClose}
        footer={null}>
        <Text type='secondary'>Add tickets with a site tag first.</Text>
      </Modal>
    )
  }

  return (
    <Modal
      title='ROI assumptions'
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText='Save'
      okButtonProps={{ disabled: !canEditRoi, loading: saving }}
      width={560}>
      <Form form={form} layout='vertical' size='small' disabled={!canEditRoi}>
        <Title level={5} style={{ marginTop: 0 }}>
          Site assumptions
        </Title>
        <Text type='secondary'>
          Values are saved for the selected site/TAR. Asset cost values act as
          defaults and are overridden by per-asset contract costs when present.
        </Text>
        <Form.Item
          name='siteId'
          label='Site'
          rules={[{ required: true }]}
          style={{ marginTop: 12 }}>
          <Select
            options={[...siteOptions]}
            onChange={handleSiteChange}
            showSearch
            optionFilterProp='label'
          />
        </Form.Item>
        <Form.Item
          name='equipmentPrepMobDemobAedPerAsset'
          label='Equipment prep / mobilization / demobilization (AED / asset)'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='traditionalInspectionAedPerAsset'
          label='Traditional inspection (AED / asset)'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='niiInspectionAedPerAsset'
          label='NII inspection (AED / asset)'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='scaffoldingAedPerAsset'
          label='Scaffolding (AED / asset)'>
          <InputNumber min={0} step={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='traditionalInspectionTeamPerAsset'
          label='Traditional inspection team size (per asset)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='hseIncidentProbPercent'
          label='HSE incident probability (per person per TAR)'>
          <InputNumber
            min={0}
            step={0.01}
            suffix='%'
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name='hseDownsideAed'
          label='HSE downside cost per incident (AED)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='criticalPathReductionPercent'
          label='Schedule reduction factor (when on critical path)'>
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Divider style={{ margin: '16px 0' }} />

        <Title level={5} style={{ marginBottom: 4 }}>
          Turnaround inputs
        </Title>
        <Form.Item name='tarDurationDays' label='TAR duration (days)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='dailyProductionAed'
          label='Daily production value (AED / day)'>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          name='inspectionOnCriticalPath'
          label='Inspection on critical path'
          valuePropName='checked'>
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
